// resources/js/src/store/postStore.ts
import { create } from "zustand";
import { postApi } from "../services/post.api";
import { ApiError } from "../services/api";
import type {
  CommunityPost,
  CreatePostPayload,
  PostReaction,
  UpdatePostPayload,
} from "../services/post.api";
import type { ReactionType } from "../components/community/components/post/interfaces/types";

/*
|--------------------------------------------------------------------------
| Feed Mode
|--------------------------------------------------------------------------
|
| - feed : الـ feed العام (community/posts)
| - mine : بوستات اليوزر المسجل دخوله (community/posts/mine)
| - user : بوستات يوزر معيّن (community/users/{encryptedId}/posts) — للبروفايل
|
| ⚠️ مهم: userId هنا string (مشفّر بـ Laravel Crypt)
| الباك إند بيفك التشفير في CommunityPostController::userPosts
|
*/

export type FeedMode =
  | { type: "feed" }
  | { type: "mine" }
  | { type: "user"; userId: string };

/*
|--------------------------------------------------------------------------
| Store Shape
|--------------------------------------------------------------------------
*/

type PostStoreState = {
  // Data
  posts: CommunityPost[];
  nextCursor: string | null;
  hasMore: boolean;

  // UI State
  isLoading: boolean;
  isLoadingMore: boolean;
  isSaving: boolean;
  error: string;

  // Feed Actions
  loadFeed: (reset?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Post CRUD
  createPost: (payload: CreatePostPayload) => Promise<CommunityPost | null>;
  updatePost: (
    id: number | string,
    payload: UpdatePostPayload
  ) => Promise<boolean>;
  deletePost: (id: number | string) => Promise<boolean>;

  // Reactions
  reactToPost: (
    postId: number | string,
    reaction: ReactionType
  ) => Promise<boolean>;

  // Local mutations
  prependPost: (post: CommunityPost) => void;
  replacePost: (post: CommunityPost) => void;
  removePost: (id: number) => void;
  incrementCommentsCount: (postId: number | string, delta: number) => void;

  clearError: () => void;
  reset: () => void;
};

/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

function parseError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Network error";
}

function parseMeta(raw: any): {
  next_cursor: string | null;
  prev_cursor: string | null;
  has_more: boolean;
  per_page: number;
} {
  const isObject = raw && typeof raw === "object" && !Array.isArray(raw);
  return {
    next_cursor: isObject ? raw.next_cursor ?? null : null,
    prev_cursor: isObject ? raw.prev_cursor ?? null : null,
    has_more: isObject ? raw.has_more ?? false : false,
    per_page: isObject ? raw.per_page ?? 15 : 15,
  };
}

function parseData<T>(raw: any): T[] {
  return Array.isArray(raw) ? raw : [];
}

/**
 * تحويل id لرقم.
 *
 * ⚠️ ملاحظة: في تطبيقك، الـ post.id رقم عادي (مش مشفّر) — الـ encryption
 * بيحصل على user.id في البروفايل بس. فالـ postId هنا رقم سليم.
 * لو لاحقاً شفّرت post.id كمان، هتحتاج تفكّه قبل المقارنة.
 */
const toNumericId = (id: number | string): number => {
  if (typeof id === "number") return id;
  const parsed = parseInt(id, 10);
  return Number.isNaN(parsed) ? -1 : parsed;
};

/*
|--------------------------------------------------------------------------
| Store Factory
|--------------------------------------------------------------------------
*/

export const createPostStore = (mode: FeedMode = { type: "feed" }) =>
  create<PostStoreState>((set, get) => {
    /**
     * helper: fetch page حسب الـ mode.
     * - mine → loadMyPosts
     * - user → loadUserPosts(userId)  ← userId string مشفّر
     * - feed → loadFeed
     */
    const fetchPage = (cursor: string | null) => {
      if (mode.type === "mine") {
        return postApi.loadMyPosts(cursor, 15);
      }
      if (mode.type === "user") {
        return postApi.loadUserPosts(mode.userId, cursor, 15);
      }
      return postApi.loadFeed(cursor, 15);
    };

    return {
      // ==================== Initial State ====================
      posts: [],
      nextCursor: null,
      hasMore: true,
      isLoading: false,
      isLoadingMore: false,
      isSaving: false,
      error: "",

      // ==================== Load Feed ====================
      loadFeed: async (reset = false) => {
        if (get().isLoading) return;

        set({
          isLoading: true,
          error: "",
          ...(reset ? { posts: [], nextCursor: null, hasMore: true } : {}),
        });

        try {
          const res = await fetchPage(null);
          const data = parseData<CommunityPost>(res?.data);
          const meta = parseMeta(res?.meta);

          set({
            posts: data,
            nextCursor: meta.next_cursor,
            hasMore: meta.has_more,
            isLoading: false,
          });
        } catch (error) {
          set({ error: parseError(error), isLoading: false });
        }
      },

      // ==================== Load More ====================
      loadMore: async () => {
        const { nextCursor, hasMore, isLoadingMore, posts } = get();
        if (!hasMore || isLoadingMore || !nextCursor) return;

        set({ isLoadingMore: true });

        try {
          const res = await fetchPage(nextCursor);
          const data = parseData<CommunityPost>(res?.data);
          const meta = parseMeta(res?.meta);

          set({
            posts: [...posts, ...data],
            nextCursor: meta.next_cursor,
            hasMore: meta.has_more,
            isLoadingMore: false,
          });
        } catch (error) {
          set({ error: parseError(error), isLoadingMore: false });
        }
      },

      // ==================== Refresh ====================
      refresh: async () => {
        set({ posts: [], nextCursor: null, hasMore: true });
        await get().loadFeed();
      },

      // ==================== Create ====================
      createPost: async (payload) => {
        set({ isSaving: true, error: "" });
        try {
          const post = await postApi.createPost(payload);
          set((state) => ({ posts: [post, ...state.posts], isSaving: false }));
          return post;
        } catch (error) {
          set({ error: parseError(error), isSaving: false });
          return null;
        }
      },

      // ==================== Update ====================
      updatePost: async (id, payload) => {
        set({ isSaving: true, error: "" });
        try {
          const post = await postApi.updatePost(id, payload);
          set((state) => ({
            posts: state.posts.map((p) => (p.id === post.id ? post : p)),
            isSaving: false,
          }));
          return true;
        } catch (error) {
          set({ error: parseError(error), isSaving: false });
          return false;
        }
      },

      // ==================== Delete ====================
      deletePost: async (id) => {
        set({ isSaving: true, error: "" });
        const numericId = toNumericId(id);
        try {
          await postApi.deletePost(id);
          set((state) => ({
            posts: state.posts.filter((p) => p.id !== numericId),
            isSaving: false,
          }));
          return true;
        } catch (error) {
          set({ error: parseError(error), isSaving: false });
          return false;
        }
      },

      // ==================== React to Post ====================
      reactToPost: async (postId, reaction) => {
        const previousPosts = get().posts;
        const numericId = toNumericId(postId);

        // ⚠️ ملاحظة: optimistic update ده تقديري فقط.
        // الـ backend بيرجّع الحالة النهائية، فمش مشكلة لو الـ optimistic
        // مش دقيق 100% (مثلاً reactions list).
        set((state) => ({
          posts: state.posts.map((p) => {
            if (p.id !== numericId) return p;

            const prevReaction = p.myReaction;
            const hadReaction = !!prevReaction && prevReaction !== null;
            const nextReaction = reaction ?? null;

            let reactionsCount = p.reactionsCount;
            if (!hadReaction && nextReaction) reactionsCount += 1;
            if (hadReaction && !nextReaction) reactionsCount -= 1;

            // ⚠️ ده تقديري — الـ backend هيصحح
            // (مش بنعرف currentUser.id هنا، بنستخدم author كـ placeholder)
            const filtered = (p.reactions ?? []).filter(
              (r) => r.user.id !== p.author.id
            );

            const nextReactions =
              nextReaction && p.author
                ? [
                    ...filtered,
                    {
                      id: Date.now(),
                      type: nextReaction,
                      user: {
                        id: p.author.id,
                        name: p.author.name,
                        avatarUrl: p.author.avatarUrl,
                      },
                    } as PostReaction,
                  ]
                : filtered;

            return {
              ...p,
              myReaction: nextReaction as ReactionType,
              reactionsCount: Math.max(0, reactionsCount),
              reactions: nextReactions,
            };
          }),
        }));

        try {
          const res = await postApi.reactToPost(postId, reaction);
          set((state) => ({
            posts: state.posts.map((p) =>
              p.id === numericId
                ? {
                    ...p,
                    myReaction: res.myReaction ?? reaction,
                    reactionsCount: res.reactionsCount,
                    reactions: (res.reactions ?? p.reactions) as PostReaction[],
                  }
                : p
            ),
          }));
          return true;
        } catch (error) {
          // rollback
          set({ posts: previousPosts, error: parseError(error) });
          return false;
        }
      },

      // ==================== Local Mutations ====================
      prependPost: (post) =>
        set((state) => ({ posts: [post, ...state.posts] })),

      replacePost: (post) =>
        set((state) => ({
          posts: state.posts.map((p) => (p.id === post.id ? post : p)),
        })),

      removePost: (id) =>
        set((state) => ({ posts: state.posts.filter((p) => p.id !== id) })),

      incrementCommentsCount: (postId, delta) => {
        const numericId = toNumericId(postId);
        set((state) => ({
          posts: state.posts.map((p) =>
            p.id === numericId
              ? { ...p, commentsCount: Math.max(0, p.commentsCount + delta) }
              : p
          ),
        }));
      },

      // ==================== Utilities ====================
      clearError: () => set({ error: "" }),

      reset: () =>
        set({
          posts: [],
          nextCursor: null,
          hasMore: true,
          isLoading: false,
          isLoadingMore: false,
          isSaving: false,
          error: "",
        }),
    };
  });

// للتوافق مع الكود القديم (الـ feed العام)
export const usePostStore = createPostStore({ type: "feed" });